import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarOff, Plus, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export default function AdminFestivos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [fecha, setFecha] = useState('');
  const [motivo, setMotivo] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/festivos-custom`, { headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (err) {
      toast.error('No se pudieron cargar los festivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!fecha) { toast.error('Selecciona una fecha'); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/festivos-custom`, { fecha, motivo }, { headers: authHeaders() });
      toast.success(`Festivo añadido: ${fecha}`);
      setFecha(''); setMotivo('');
      fetchList();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al añadir festivo';
      toast.error(typeof msg === 'string' ? msg : 'Error al añadir festivo');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este festivo?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/admin/festivos-custom/${id}`, { headers: authHeaders() });
      toast.success('Festivo eliminado');
      fetchList();
    } catch (err) {
      toast.error('No se pudo eliminar');
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return iso; }
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#374B67] flex items-center gap-2">
              <CalendarOff size={20} /> Gestión de Festivos
            </h1>
            <p className="text-sm text-slate-500">Días no laborables adicionales para excluir del calendario de recogida.</p>
          </div>
        </div>

        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-slate-200 p-4 mb-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Añadir festivo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-600">Fecha</Label>
              <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
                data-testid="festivo-fecha-input" required />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs text-slate-600">Motivo (opcional)</Label>
              <Input placeholder="Ej: Fiesta local" value={motivo} onChange={e => setMotivo(e.target.value)}
                data-testid="festivo-motivo-input" maxLength={200} />
            </div>
            <div>
              <Button type="submit" disabled={adding} className="w-full bg-[#009940] hover:bg-[#007F35] text-white"
                data-testid="festivo-add-button">
                {adding ? <><Loader2 size={14} className="mr-2 animate-spin" /> Añadiendo…</> : <><Plus size={14} className="mr-2" /> Añadir festivo</>}
              </Button>
            </div>
          </div>
        </form>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Festivos configurados</h3>
            <span className="text-xs text-slate-500" data-testid="festivos-count">{items.length} registrados</span>
          </div>
          {loading ? (
            <div className="text-center py-12 text-slate-500">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="festivos-empty">No hay festivos configurados</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Fecha</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Motivo</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Creado por</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-600">Acción</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`festivo-row-${it.fecha}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{it.fecha}<div className="text-[11px] text-slate-400 capitalize">{fmt(it.fecha)}</div></td>
                    <td className="px-4 py-3 text-slate-700">{it.motivo || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{it.created_by || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" disabled={busyId === it.id}
                        onClick={() => handleDelete(it.id)} className="text-red-600 hover:bg-red-50"
                        data-testid={`festivo-delete-${it.fecha}`}>
                        {busyId === it.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
